import * as React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../lib/cn';
import { Input } from './Input';

/**
 * SearchField — the search input for tables and lists.
 *
 * THE SEVENTEENTH IMPLEMENTATION, AND THE LAST ONE. Before this, `@sighthost/ui`
 * shipped 64 components and not one of them was a search box, so seventeen pages
 * each drew their own: a bare `<input type="search">` with an absolutely
 * positioned lucide `Search` behind it, in four mutually incompatible shapes.
 * A bare element inherits none of the focus ring, none of the 40px height and
 * none of the 44px touch target (CLAUDE.md §4.2) — which is exactly what those
 * seventeen shipped.
 *
 * LAYOUT ONLY (architect ruling AR-3). It does not filter, it does not fetch,
 * and — deliberately — it does not debounce. Debouncing is a timing concern that
 * belongs to the hook that owns the data (`useTableFilters`), not to a
 * presentational control. A component that silently delayed its own `onChange`
 * would be impossible to compose with a deferred value without double-deferring.
 *
 * AR-6 NOTE. `pending` is a *rendering* flag driven by the consumer's
 * `isSearching`. It never means "a request is in flight" — under Rule T-3 a
 * keystroke must never produce one.
 */

export interface SearchFieldProps
  extends Omit<
    React.ComponentProps<'input'>,
    'type' | 'value' | 'onChange' | 'prefix' | 'size'
  > {
  value: string;
  onValueChange: (next: string) => void;
  /**
   * Name the fields actually searched — "Search email, name, company".
   *
   * Not decorative. When a filtered table comes back empty, the placeholder is
   * the only thing on screen that explains what was and was not looked at; a
   * bare "Search" makes an empty result unexplainable. Sibling of Rule T-4.
   */
  placeholder?: string;
  /** Renders the pending affordance. Drive from `useTableFilters().isSearching`. */
  pending?: boolean;
  /** Show the clear button when non-empty. Default true. */
  clearable?: boolean;
  /** Accessible name when there is no visible label. */
  label?: string;
  className?: string;
}

const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      value,
      onValueChange,
      placeholder = 'Search',
      pending = false,
      clearable = true,
      label = 'Search',
      className,
      disabled,
      onKeyDown,
      ...props
    },
    forwardedRef,
  ) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    // Merge the forwarded ref with our own: the clear button has to return
    // focus to the input, and a consumer may still want the node.
    const setRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    const showClear = clearable && value.length > 0 && !disabled;

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      // Escape clears rather than blurring. Clearing is what a user wants from a
      // search box, and the browser's own Escape-clears behaviour on
      // `type="search"` is inconsistent across engines — so we own it.
      if (e.key === 'Escape' && value.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        onValueChange('');
      }
      onKeyDown?.(e);
    }

    return (
      <div className={cn('relative', className)}>
        <Search
          className={cn(
            'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
            'text-muted-foreground transition-opacity',
            // Swapped for the spinner, which occupies the same box. Both drawn
            // at once would read as two icons crossfading.
            pending ? 'opacity-0' : 'opacity-100',
          )}
          aria-hidden
        />

        <Input
          ref={setRef}
          type="search"
          // `type="search"` already conveys the role to assistive tech. Adding
          // role="searchbox" on top makes some screen readers announce it twice.
          value={value}
          disabled={disabled}
          aria-label={props['aria-labelledby'] ? undefined : label}
          placeholder={placeholder}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn(
            'pl-9',
            // The trailing gutter is reserved unconditionally, not only when the
            // clear button is present. Sizing it on `showClear` would reflow the
            // text the instant the first character lands.
            clearable ? 'pr-9' : 'pr-3',
            // Chrome/Safari draw their own clear affordance on type="search";
            // ours is keyboard-reachable and themed, so suppress theirs.
            '[&::-webkit-search-cancel-button]:appearance-none',
          )}
          {...props}
        />

        {showClear && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              onValueChange('');
              innerRef.current?.focus();
            }}
            className={cn(
              'absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center',
              'rounded text-muted-foreground transition-colors',
              'hover:bg-muted hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            )}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}

        {/*
          Pending lives in the icon, not in an extra element: the gutters are
          already reserved, so swapping the icon's opacity cannot shift layout.
          A spinner added beside the input would move the text on every keystroke.
        */}
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full',
            'border-2 border-primary border-r-transparent transition-opacity motion-safe:animate-spin',
            pending ? 'opacity-100' : 'opacity-0',
          )}
        />

        {/*
          The result count is announced by the table's caption (Rule T-4), not
          here — one live region per filtered view, or screen readers narrate the
          same change twice.
        */}
      </div>
    );
  },
);

SearchField.displayName = 'SearchField';

export { SearchField };
